import type { EnergyLabelInfo, PisInfo } from "../types";

const DOWNLOAD_BASE = "https://www.lg.com/uk/lgecs.downloadFile.ldwf";

export function buildDownloadUrl(doc: PisInfo): string {
  const params = new URLSearchParams({
    DOC_ID: doc.docId,
    ORIGINAL_NAME_b1_a1: doc.originalName,
    FILE_NAME: doc.fileName,
    TC: "DwnCmd",
    GSRI_DOC: "GSRI",
    SPEC_DOWNLOAD: "N",
  });
  return `${DOWNLOAD_BASE}?${params.toString()}`;
}

function normalizeImageUrl(url: string): string {
  const normalized = url.replace(
    /^(?:(?:https?:\/\/)?www\.lg\.com)?\/?content\/dam\/channel\/wcms\/(lg5-common-gp\/images\/products\/energy-label\/)/i,
    "/$1",
  );
  if (normalized !== url) return normalizeImageUrl(normalized);
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("www.")) return `https://${url}`;
  return `https://www.lg.com${url.startsWith("/") ? "" : "/"}${url}`;
}

// 3st Spec §4-6 — 에너지 등급 PDF 텍스트 버튼. 타이틀=고객이 물어본 SKU,
// 링크=닷컴 pdf명(originalName), 해당 SKU 라벨 개수만큼 노출.
export interface EnergyDoc {
  originalName: string;
  docId?: string;
  fileName?: string;
  url?: string;
}

export function EnergyLabelButtons({
  title,
  docs,
}: {
  title: string;
  docs: EnergyDoc[] | undefined;
}) {
  const looksLikeImageAsset = (s: string) => {
    const t = String(s || "");
    if (/\.(svg|png|jpe?g|webp)(\?|$)/i.test(t)) return true;
    return /energy-label|label-energy/i.test(t);
  };
  const links = (docs || [])
    .map((doc) => {
      const rawName = String(doc.originalName || "").trim();
      const href =
        doc.url ||
        (doc.docId && doc.fileName
          ? buildDownloadUrl({
              docId: doc.docId,
              fileName: doc.fileName,
              originalName: doc.originalName,
            })
          : "");
      if (!href) return null;
      // href 자체가 이미지 에셋이면 텍스트 링크로 쓰지 않음(이미지는 energyLabel 슬롯).
      if (looksLikeImageAsset(href)) {
        return null;
      }
      // 이미지 경로·raw URL 이 버튼 라벨로 노출되지 않도록 차단.
      const name =
        !rawName || looksLikeImageAsset(rawName)
          ? doc.fileName && !looksLikeImageAsset(doc.fileName)
            ? doc.fileName
            : "Energy label"
          : rawName;
      return { name, href };
    })
    .filter((l): l is { name: string; href: string } => !!l && !!l.href);
  if (!links.length) return null;
  // guide ai-lists "에너지등급 문의 UI" — .btn-text > .ico 텍스트 링크 아웃(MR 388 퍼블).
  return (
    <div className="ac-spec__energy">
      <h3 className="ac-spec__energy-title">{title}</h3>
      <div className="ai-gp1-detail-container ac-spec__energy-docs">
        {links.map((l, i) => (
          <a
            key={l.href || i}
            href={l.href}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-text"
          >
            <span className="ico">{l.name}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// NCMS ProductEnergyNeo — variation="icon"
// 에너지 라벨 아이콘(SVG/PNG 이미지) + 클릭 시 에너지 문서 다운로드
export function EnergyIcon({ info }: { info: EnergyLabelInfo | undefined | null }) {
  if (!info) return null;

  const imageUrl = info.imageUrl;
  const energyDocs = [info.ukDoc, info.euDoc].filter(Boolean) as PisInfo[];

  if (!imageUrl && !info.grade) return null;

  const imgEl = imageUrl ? (
    <img
      src={normalizeImageUrl(imageUrl)}
      alt={info.grade ? `Energy ${info.grade}` : "Energy label"}
      className="ac-energy__img"
      draggable={false}
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  ) : info.grade ? (
    <EnergyBadge grade={info.grade} />
  ) : null;

  if (!imgEl) return null;

  if (energyDocs.length === 1) {
    return (
      <a
        href={buildDownloadUrl(energyDocs[0])}
        target="_blank"
        rel="noreferrer noopener"
        className="ac-energy"
      >
        {imgEl}
      </a>
    );
  }

  if (energyDocs.length > 1) {
    return (
      <span className="ac-energy">
        {imgEl}
        <span className="ac-energy__docs">
          {energyDocs.map((doc) => (
            <a
              key={doc.docId}
              href={buildDownloadUrl(doc)}
              target="_blank"
              rel="noreferrer noopener"
              className="ac-energy__doc-link"
            >
              {doc === info.ukDoc ? "UK" : "EU"}
            </a>
          ))}
        </span>
      </span>
    );
  }

  return <span className="ac-energy">{imgEl}</span>;
}

// NCMS ProductEnergyNeo — variation="text"
// Product Information Sheet 텍스트 링크 (클릭 시 다운로드)
export function ProductInfoSheet({ info }: { info: EnergyLabelInfo | undefined | null }) {
  if (!info?.pis) return null;
  const pis = info.pis;
  return (
    <a
      href={buildDownloadUrl(pis)}
      target="_blank"
      rel="noreferrer noopener"
      className="ac-energy__pis"
    >
      Product
      <br />
      information sheet
    </a>
  );
}

// NCMS ProductEnergyWrapperNeo — 에너지 아이콘 + PIS 텍스트를 한 줄로 묶음
export function EnergyPisRow({ info }: { info: EnergyLabelInfo | undefined | null }) {
  if (!info) return null;
  const hasIcon = !!(info.imageUrl || info.grade);
  const hasPis = !!info.pis;
  if (!hasIcon && !hasPis) return null;

  return (
    <div className="ac-energy">
      {hasIcon && <EnergyIcon info={info} />}
      {hasPis && <ProductInfoSheet info={info} />}
    </div>
  );
}

// 하위 호환: 기존 EnergyBadge (grade 문자열만 있을 때 fallback)
export function EnergyBadge({ grade }: { grade: string }) {
  const g = String(grade ?? "").trim().toUpperCase();
  if (!g) return null;
  const validGrade = /^[A-G]$/.test(g);
  const cls = validGrade
    ? `ac-energy-badge ac-energy-badge--${g}`
    : "ac-energy-badge";
  return <span className={cls}>{g}</span>;
}

// 하위 호환: 기존 EnergyPis (다른 카드에서 아직 사용)
export function EnergyPis({
  info,
  compact = false,
}: {
  info: EnergyLabelInfo | undefined | null;
  compact?: boolean;
}) {
  if (!info) return null;
  const hasAnything = info.grade || info.imageUrl || info.pis || info.euDoc || info.ukDoc;
  if (!hasAnything) return null;

  return (
    <div className="ac-energy" style={compact ? undefined : { marginTop: 4 }}>
      <EnergyPisRow info={info} />
    </div>
  );
}

export function gradeToEnergyInfo(
  grade: string | undefined | null,
): EnergyLabelInfo | undefined {
  if (!grade || typeof grade !== "string") return undefined;
  const trimmed = grade.trim();
  if (trimmed.length <= 2 && /^[A-Ga-g]$/.test(trimmed)) return { grade: trimmed };
  if (trimmed.includes("/") || /\.(svg|png|jpg|webp)$/i.test(trimmed)) {
    const url = normalizeImageUrl(trimmed);
    return { imageUrl: url };
  }
  return { grade: trimmed };
}
